// The one comment surface. Deck notes (DeckSidePanel) and replay row threads
// (ReplayOverlay) both render through these: a forum-style row with the
// author's avatar, name, crown title and relative time, a Discord-style hover
// pill (reply, like, delete) straddling the row's top edge, an in-flow like
// count under the body, plus the chat-app composer with the send arrow
// anchored inside the input frame.
//
// Anything that differs between the two surfaces rides in as a prop or a
// slot (the deck's quoted-text cross-link is a `quote` node, its
// reply-to-quote draft is composer children). Nothing here knows about decks
// or replays.
import MarkdownEditor from '../MarkdownEditor';
import { useState, useRef, useLayoutEffect } from 'preact/hooks';
import { renderInline } from '../MarkdownNotes';
import PlayerTitle from '../PlayerTitle';
import { timeAgo } from '../../utils/arena/timeFormat';
import AvatarImage from '../ui/AvatarImage';
import { GOLD, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD, MENU_BG, RADIUS, innerRadius } from '../../lib/medievalTheme';
import { twemojiUrl } from '../../utils/emojiArt';
import { k, t } from '../../localization/t';

// A comment body as inline markdown. Bare URLs become clickable links,
// `[card](card:id)` mentions become pills, **bold**, *italic*, `code`, and
// element words all work. Block-level constructs (#, -, 1.) are
// intentionally skipped. Comments stay conversational. Newlines render as
// `<br/>` between inline runs.
export function renderCommentBody(body, ctx) {
  const lines = (body || '').split('\n');
  const out = [];
  lines.forEach((line, i) => {
    if (i > 0) out.push(<br key={`br-${i}`} />);
    out.push(<span key={`l-${i}`}>{renderInline(line, ctx)}</span>);
  });
  return out;
}

// Discord-style grouping: a message continues the previous one when the same
// player posted it within a few minutes and it isn't a reply. Continued rows
// drop the avatar + byline and sit tight under the row above; a new group
// gets breathing room above instead of a divider line.
const GROUP_WINDOW_MS = 7 * 60 * 1000;
export function continuesPrevious(prev, cur) {
  if (!prev || !cur || !cur.playerId || prev.playerId !== cur.playerId) return false;
  if (cur.replyTo) return false;
  return Math.abs(new Date(cur.createdAt) - new Date(prev.createdAt)) < GROUP_WINDOW_MS;
}

// The quick-reaction set, mirrored by REACTIONS in the server's globalChat.
// The first QUICK_REACTIONS sit in the toolbar itself, Discord-style; the
// rest unfold inside the same pill on demand.
export const REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '👎'];
export const QUICK_REACTIONS = 3;
const REACTION_NAMES = { '👍': k('Thumbs up'), '❤️': k('Heart'), '😂': k('Laugh'), '😮': k('Wow'), '😢': k('Sad'), '👎': k('Thumbs down') };
// Toolbar tooltip: the first one waits so a pass across the pill stays
// quiet; while one is up, the next button gets it at once, sliding over.
// Once the pointer has left, the pill stays warm this long.
const TIP_DELAY_MS = 400;
const TIP_WARM_MS = 300;

// One emoji drawn with the bundled Twemoji art. The alt text is the emoji
// itself, so a missing file degrades to the platform glyph.
function EmojiGlyph({ emoji, size }) {
  return <img src={twemojiUrl(emoji)} alt={emoji} draggable={false} style={{ width: size, height: size, display: 'block' }} />;
}

// Avatar column (1.55cqw) plus the gap-2 to the body column: where the
// body text starts, and where a reply reference lines up above it.
const REPLY_INDENT = '2.05cqw';

// A one-line, plain-text rendering of a message body for previews:
// links keep their label, emphasis markers drop.
export function plainPreview(text) {
  return (text || '')
    .replace(/\[([^\]\n]+)\]\([^)\s]+\)/g, '$1')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/`(.+?)`/g, '$1')
    .replace(/(^|[^*])\*([^*\s][^*]*?)\*(?!\*)/g, '$1$2')
    .replace(/\s+/g, ' ')
    .trim();
}

// The message a reply points at, Discord-style: mini avatar, author, and a
// truncated plain-text preview on one line. Clicking opens the thread.
export function ReplyReference({ avatarUrl, name, preview, onClick }) {
  const initial = (name || '?').slice(0, 1).toUpperCase();
  return (
    <div
      role="button"
      tabIndex={0}
      className="flex items-center gap-1 min-w-0 cursor-pointer"
      title={t('View this thread')}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }}
      onMouseEnter={(e) => { e.currentTarget.querySelector('[data-preview]').style.color = TEXT_BODY; }}
      onMouseLeave={(e) => { e.currentTarget.querySelector('[data-preview]').style.color = TEXT_MUTED; }}
    >
      <span
        className="shrink-0 overflow-hidden flex items-center justify-center"
        style={{ width: '0.9cqw', height: '0.9cqw', borderRadius: '50%', background: 'rgba(180, 140, 60, 0.14)', color: ACCENT_GOLD, fontSize: '0.45cqw', fontWeight: 700 }}
      >
        {avatarUrl ? <AvatarImage src={avatarUrl} className="w-full h-full" /> : initial}
      </span>
      <span className="shrink-0 font-semibold" style={{ fontSize: '0.58cqw', color: ACCENT_GOLD, lineHeight: 1 }}>{name || t('Unknown')}</span>
      <span data-preview className="truncate transition-colors" style={{ fontSize: '0.58cqw', color: TEXT_MUTED, lineHeight: 1 }}>{plainPreview(preview)}</span>
    </div>
  );
}

// Single comment row. Discord/forum-style: no per-message chrome, subtle
// hover tint, dividers between rows.
export function CommentRow({
  comment: c,
  avatarUrl,
  continued = false,
  sorceryCards,
  canDelete,
  isHovered,
  onHoverChange,
  likeBusy,
  onOpenProfile,
  onDelete,
  onToggleLike,
  quote = null,
  // Rendered under the body: a card the message carries (a shared replay).
  attachment = null,
  hasTitle = false,
  onTitleResolve,
  onCardClick,
  onCardHover,
  onCardHoverEnd,
  // Deep-link landing: a notification click scrolls this row into view
  // (parents find it by data-comment-id) and holds a plain gold wash on
  // it for a beat so the eye lands on the right comment. No edge bar: the
  // wash is a transient cue, not row chrome.
  highlighted = false,
  onPlayerClick,
  onReply,
  showLike = true,
  // Rendered above the byline as the message this one replies to, joined
  // to the avatar by a curved spine (a `ReplyReference`).
  reply = null,
  // Emoji reactions instead of the single like: `reactions` is the wire
  // list [{ emoji, count, me }], `onReact(emoji)` toggles one, and the
  // quick picker's open state is the parent's so only one row shows it.
  reactions = null,
  onReact,
  pickerOpen = false,
  onPickerToggle,
  // Thread entry: a toolbar button that opens this message as a thread,
  // and an in-flow slot under the chips for the replies pill.
  onOpenThread,
  onForward,
  footer = null,
  // Own-message editing: `onEdit` puts a pencil in the toolbar, and while
  // `editor` is set it replaces the body (the inline editor node).
  onEdit,
  editor = null,
  // Copy for the inline delete prompt the pill morphs into.
  deletePrompt = t('delete this comment?'),
}) {
  const initials = (c.playerName || '?').slice(0, 1).toUpperCase();
  const playerId = c.playerId;
  const canOpenProfile = !!onOpenProfile && !!playerId;
  // Delete asks first, inside the pill itself: the tools swap for a
  // "delete this?" strip with delete / keep. Shift+click on the trash skips
  // it, Discord-style. Leaving the row drops the prompt with the pill.
  const [confirming, setConfirming] = useState(false);
  const [tip, setTip] = useState(null);
  const pillRef = useRef(null);
  const tipRef = useRef(null);
  const tipTimers = useRef({ show: null, cool: null, warm: false });

  // Centre on the control, but never past the pill's own edges: the pill
  // already keeps clear of the panel wall, and the panel clips.
  useLayoutEffect(() => {
    const el = tipRef.current;
    if (!el || !tip) return;
    const half = el.offsetWidth / 2;
    const width = pillRef.current?.offsetWidth || 0;
    const x = Math.min(Math.max(tip.x, half), width - half);
    el.style.setProperty('--tip-x', `${x}px`);
    // The caret keeps pointing at the control when the body is held back.
    el.style.setProperty('--tip-caret', `${x - tip.x}px`);
    // Above the pill unless the list's top edge is too close: a pill that
    // has ridden up to the edge gets its tooltip underneath instead.
    const scroller = scrollParent(pillRef.current);
    const room = scroller ? pillRef.current.getBoundingClientRect().top - scroller.getBoundingClientRect().top : Infinity;
    el.dataset.side = room < el.offsetHeight * 1.5 ? 'below' : 'above';
  }, [tip]);

  // `x` is the control's centre measured from the pill's RIGHT edge. The
  // pill is right-anchored and grows leftward as the reactions unfold, so
  // a control right of the fold keeps its distance from that edge and its
  // tooltip holds still through the morph.
  const showTip = (e, label) => {
    const t = tipTimers.current;
    const pill = pillRef.current?.getBoundingClientRect();
    const btn = e.currentTarget.getBoundingClientRect();
    const x = pill ? pill.right - (btn.left + btn.width / 2) : 0;
    clearTimeout(t.cool);
    clearTimeout(t.show);
    const place = () => setTip((prev) => ({ label, x, visible: true, instant: t.warm && !prev?.visible }));
    if (t.warm) place();
    else t.show = setTimeout(() => { t.warm = true; place(); }, TIP_DELAY_MS);
  };
  const hideTip = () => {
    const t = tipTimers.current;
    clearTimeout(t.show);
    setTip((prev) => (prev?.visible ? { ...prev, visible: false } : prev));
    clearTimeout(t.cool);
    t.cool = setTimeout(() => { t.warm = false; }, TIP_WARM_MS);
  };
  const tipOn = (label) => ({ onMouseEnter: (e) => showTip(e, label), onMouseLeave: hideTip, onFocus: (e) => showTip(e, label), onBlur: hideTip });
  const toolbarVisible = isHovered || pickerOpen || confirming;
  const padTop = continued ? '0.1cqw' : '0.8cqw';
  return (
    <div
      className="relative"
      data-comment-id={c.id}
      onMouseEnter={() => onHoverChange?.(c.id)}
      onMouseLeave={() => { onHoverChange?.(null); if (pickerOpen) onPickerToggle?.(false); setConfirming(false); }}
      style={{
        padding: `${padTop} 0.85cqw 0.1cqw`,
        background: highlighted
          ? 'rgba(212,168,67,0.09)'
          : isHovered ? 'rgba(255,255,255,0.025)' : 'transparent',
        transition: 'background 600ms ease',
      }}
    >
      {/* Hover toolbar (Discord-style): a solid pill that straddles the row's
          top edge, so it sits over the gap to the row above instead of over
          this row's text. Quick reactions lead, the remaining ones unfold
          inside the pill, then reply, thread and delete.

          The anchor is sticky inside the row: while the row's top is
          scrolled out of the list, the pill rides down with the viewport
          edge instead of being clipped, and stops at the row's bottom, so
          it never sits on the next message. */}
      <div
        className="chat-tools-anchor"
        style={{ position: 'sticky', top: TOOLBAR_STICK, height: 0, zIndex: 5, marginTop: `-${padTop}` }}
      >

      <div
        ref={pillRef}
        className={`chat-tools absolute flex items-center${toolbarVisible ? ' is-visible' : ''}`}
        style={{
          top: 0,
          right: 0,
          // No gap here: the collapsed confirm layer is still a flex item,
          // and a gap beside it would pad the pill's right edge. Each
          // layer spaces its own controls.
          padding: TOOLBAR_PAD,
          borderRadius: TOOLBAR_RADIUS,
          background: MENU_BG,
          border: `1px solid ${GOLD} 0.3)`,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.45)',
        }}
      >
        {/* Two layers, both always mounted, so the swap is a cross-fade
            with a width morph (grid track 1fr <-> 0fr) rather than a
            mount/unmount snap, and it reverses cleanly mid-flight. */}
        <div className={`chat-tools-layer${confirming ? ' is-out' : ''}`} aria-hidden={confirming}>
        <div className="chat-tools-layer-inner flex items-center" style={{ gap: '0.1cqw' }}>
        {onReact ? (
          <>
            {REACTIONS.slice(0, QUICK_REACTIONS).map((emoji) => (
              <ReactionToolButton key={emoji} emoji={emoji} mine={!!reactions?.some((r) => r.emoji === emoji && r.me)} onClick={() => onReact(emoji)} tipOn={tipOn} />
            ))}
            <div className={`chat-tools-more${pickerOpen ? ' is-open' : ''}`} aria-hidden={!pickerOpen}>
              <div className="chat-tools-more-inner flex items-center" style={{ gap: '0.1cqw' }}>
                {REACTIONS.slice(QUICK_REACTIONS).map((emoji, i) => (
                  <ReactionToolButton
                    key={emoji}
                    emoji={emoji}
                    mine={!!reactions?.some((r) => r.emoji === emoji && r.me)}
                    tabIndex={pickerOpen ? 0 : -1}
                    style={{ transitionDelay: pickerOpen ? `${i * 35}ms` : '0ms' }}
                    onClick={() => { onReact(emoji); onPickerToggle?.(false); }}
                    tipOn={tipOn}
                  />
                ))}
              </div>
            </div>
            <ToolbarButton
              onClick={() => {
                onPickerToggle?.(!pickerOpen);
                // The tooltip is up while this is clicked: reword it in
                // place rather than leaving the old verb on screen.
                setTip((prev) => (prev?.visible ? { ...prev, label: pickerOpen ? t('More reactions') : t('Fewer reactions') } : prev));
              }}
              {...tipOn(pickerOpen ? t('Fewer reactions') : t('More reactions'))}
              aria-label={t('More reactions')}
              aria-expanded={pickerOpen}
              className={pickerOpen ? 'is-open' : ''}
              style={{ color: pickerOpen ? ACCENT_GOLD : TEXT_MUTED }}
            >
              <svg className="chat-tool-more-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" style={{ width: '0.75cqw', height: '0.75cqw' }}>
                <path d="M12 5v14M5 12h14" />
              </svg>
            </ToolbarButton>
            <span aria-hidden style={{ width: '1px', height: '0.9cqw', margin: '0 0.1cqw', background: `${GOLD} 0.25)` }} />
          </>
        ) : null}
        {onReply ? (
          <ToolbarButton onClick={() => onReply(c)} {...tipOn(t('Reply'))} aria-label={t('Reply')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '0.8cqw', height: '0.8cqw' }}>
              <polyline points="9 17 4 12 9 7" />
              <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
            </svg>
          </ToolbarButton>
        ) : null}
        {onForward ? (
          <ToolbarButton onClick={() => onForward(c)} {...tipOn(t('Forward'))} aria-label={t('Forward')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '0.8cqw', height: '0.8cqw' }}>
              <polyline points="15 17 20 12 15 7" />
              <path d="M4 18v-2a4 4 0 0 1 4-4h12" />
            </svg>
          </ToolbarButton>
        ) : null}
        {onEdit ? (
          <ToolbarButton onClick={onEdit} {...tipOn(t('Edit'))} aria-label={t('Edit message')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '0.75cqw', height: '0.75cqw' }}>
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
            </svg>
          </ToolbarButton>
        ) : null}
        {onOpenThread ? (
          <ToolbarButton onClick={onOpenThread} {...tipOn(t('View thread'))} aria-label={t('View thread')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '0.75cqw', height: '0.75cqw' }}>
              <path d="M14 9a2 2 0 0 1-2 2H6l-4 4V4c0-1.1.9-2 2-2h8a2 2 0 0 1 2 2z" />
              <path d="M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1" />
            </svg>
          </ToolbarButton>
        ) : null}
        {showLike && !reactions ? (
          <ToolbarButton
            disabled={likeBusy}
            onClick={() => onToggleLike?.(c.id)}
            {...tipOn(c.liked ? t('Unlike') : t('Like'))}
            aria-label={c.liked ? t('Unlike comment') : t('Like comment')}
            style={{ color: c.liked ? '#e85050' : TEXT_MUTED, opacity: likeBusy ? 0.6 : 1 }}
          >
            <HeartIcon filled={c.liked} size="0.8cqw" />
          </ToolbarButton>
        ) : null}
        {canDelete ? (
          <ToolbarButton
            onClick={(e) => { if (e.shiftKey) onDelete?.(c.id); else { onPickerToggle?.(false); hideTip(); setConfirming(true); } }}
            {...tipOn(t('Delete'))}
            aria-label={t('Delete comment')}
            className="chat-tool-danger"
            style={{ color: '#c45050' }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '0.7cqw', height: '0.7cqw' }}>
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
            </svg>
          </ToolbarButton>
        ) : null}
        </div>
        </div>
        <div className={`chat-tools-layer chat-tools-confirm${confirming ? '' : ' is-out'}`} aria-hidden={!confirming}>
          <div className="chat-tools-layer-inner flex items-center" style={{ gap: '0.1cqw' }}>
            <span style={{ fontSize: '0.55cqw', color: TEXT_BODY, padding: '0 0.3cqw 0 0.45cqw', whiteSpace: 'nowrap' }}>{deletePrompt}</span>
            <ToolbarButton
              className="chat-tool-danger"
              tabIndex={confirming ? 0 : -1}
              onClick={() => { setConfirming(false); onDelete?.(c.id); }}
              style={{ width: 'auto', padding: '0 0.4cqw', fontSize: '0.55cqw', fontWeight: 600, color: '#e05a5a' }}
            >
              {t('delete')}
            </ToolbarButton>
            <ToolbarButton tabIndex={confirming ? 0 : -1} onClick={() => setConfirming(false)} style={{ width: 'auto', padding: '0 0.4cqw', fontSize: '0.55cqw' }}>
              {t('keep')}
            </ToolbarButton>
          </div>
        </div>
        {/* One tooltip for the whole pill, above the hovered control. Its
            x is set from the control's centre, so moving along the pill
            slides it rather than swapping it. */}
        {tip ? (
          <div
            ref={tipRef}
            role="tooltip"
            aria-hidden
            className={`chat-tip${tip.visible ? ' is-visible' : ''}${tip.instant ? ' is-instant' : ''}`}
            style={{ background: MENU_BG, border: `1px solid ${GOLD} 0.3)`, color: TEXT_BODY, borderRadius: RADIUS.sm, padding: '0.15cqw 0.45cqw', fontSize: '0.55cqw', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.45)' }}
          >
            {tip.label}
          </div>
        ) : null}
      </div>
      </div>
      {reply && !continued ? (
        <div className="relative flex items-center min-w-0" style={{ paddingLeft: REPLY_INDENT, height: '1.1cqw', marginBottom: '0.2cqw' }}>
          <span
            aria-hidden
            className="absolute"
            style={{
              left: '0.775cqw',
              top: '50%',
              bottom: '-0.2cqw',
              width: '0.95cqw',
              borderLeft: '2px solid rgba(166, 160, 155, 0.28)',
              borderTop: '2px solid rgba(166, 160, 155, 0.28)',
              borderTopLeftRadius: '0.45cqw',
            }}
          />
          {reply}
        </div>
      ) : null}
      <div className="flex items-start gap-2">
        {continued ? (
          <span aria-hidden className="shrink-0" style={{ width: '1.55cqw' }} />
        ) : (
        <button
          type="button"
          className="notes-profile-link shrink-0 overflow-hidden"
          style={{
            width: '1.55cqw',
            height: '1.55cqw',
            borderRadius: '50%',
            background: 'rgba(180, 140, 60, 0.12)',
            border: `1px solid ${GOLD} 0.32)`,
            padding: 0,
          }}
          onClick={() => canOpenProfile && onOpenProfile(playerId)}
          title={canOpenProfile ? t("Open {name}'s profile", { name: c.playerName || t('player') }) : undefined}
          disabled={!canOpenProfile}
        >
          {avatarUrl ? (
            <AvatarImage src={avatarUrl} className="w-full h-full" />
          ) : (
            <span
              className="arena-heading"
              style={{
                display: 'flex',
                width: '100%',
                height: '100%',
                alignItems: 'center',
                justifyContent: 'center',
                color: ACCENT_GOLD,
                fontSize: '0.75cqw',
                fontWeight: 700,
              }}
            >
              {initials}
            </span>
          )}
        </button>
        )}

        <div className="flex-1 min-w-0">
          {continued ? null : (
          <div className="flex items-baseline gap-2 min-w-0" style={{ lineHeight: 1 }}>
            <button
              type="button"
              className="notes-profile-link arena-heading font-bold truncate"
              onClick={() => canOpenProfile && onOpenProfile(playerId)}
              disabled={!canOpenProfile}
              style={{
                color: ACCENT_GOLD,
                fontSize: '0.72cqw',
                letterSpacing: '0.04em',
                background: 'transparent',
                padding: 0,
                textAlign: 'left',
                lineHeight: 1,
              }}
              onMouseEnter={(e) => { if (canOpenProfile) e.currentTarget.style.color = '#f0c870'; }}
              onMouseLeave={(e) => { if (canOpenProfile) e.currentTarget.style.color = ACCENT_GOLD; }}
              title={canOpenProfile ? t("Open {name}'s profile", { name: c.playerName || t('player') }) : undefined}
            >
              {c.playerName || t('Unknown')}{hasTitle ? ',' : ''}
            </button>
            <span className="text-[0.55cqw] shrink-0" style={{ color: TEXT_MUTED, lineHeight: 1 }}>{timeAgo(c.createdAt)}</span>
          </div>
          )}
          {playerId && !continued ? (
            <PlayerTitle
              player={{ id: playerId, displayedCrownAvatarId: c.playerDisplayedCrownAvatarId, isFounder: !!c.playerIsFounder, isQaTester: !!c.playerIsQaTester }}
              sorceryCards={sorceryCards}
              prefix=""
              // Sit tight against the name (so the byline reads as one unit)
              // and add a clear gap below so the title doesn't visually run
              // into the comment body.
              style={{ fontSize: '0.55cqw', display: 'block', lineHeight: 1, marginTop: '0.05cqw', marginBottom: '0.4cqw' }}
              onResolve={(has) => onTitleResolve?.(c.id, has)}
            />
          ) : null}

          {quote}

          {editor || (
            <div className="text-xs whitespace-pre-wrap leading-relaxed" style={{ color: TEXT_BODY }}>
              {renderCommentBody(c.body, { onCardClick, onCardHover, onCardHoverEnd, onPlayerClick })}
              {c.editedAt ? (
                <span className="text-[0.5cqw]" style={{ color: TEXT_MUTED, marginLeft: '0.3cqw' }} title={t('Edited {time}', { time: timeAgo(c.editedAt) })}>{t('(edited)')}</span>
              ) : null}
            </div>
          )}
          {attachment}

          {/* Reaction chips, in flow under the body the way Discord lays out
              reactions. Shown while a count is non-zero so the number never
              competes with the text for the row's top-right corner. */}
          {reactions && reactions.length ? (
            <div className="flex flex-wrap items-center" style={{ gap: '0.25cqw', marginTop: '0.3cqw' }}>
              {reactions.map((r) => (
                <button
                  key={r.emoji}
                  type="button"
                  className={`chat-reaction-chip inline-flex items-center${r.me ? ' is-mine' : ''}`}
                  onClick={() => onReact?.(r.emoji)}
                  title={r.me ? t('Remove your reaction') : t('React')}
                  aria-label={`${r.emoji} ${r.count}`}
                  aria-pressed={r.me}
                  style={{
                    gap: '0.3cqw',
                    fontSize: '0.6cqw',
                    padding: '0.1cqw 0.45cqw',
                    borderRadius: RADIUS.sm,
                  }}
                >
                  <EmojiGlyph emoji={r.emoji} size="0.8cqw" />
                  <span className="tabular-nums" style={{ fontWeight: r.me ? 600 : 500 }}>{r.count}</span>
                </button>
              ))}
            </div>
          ) : null}
          {footer}
          {showLike && !reactions && c.likes > 0 ? (
            <button
              type="button"
              className="inline-flex items-center gap-1"
              disabled={likeBusy}
              onClick={() => onToggleLike?.(c.id)}
              style={{
                marginTop: '0.3cqw',
                fontSize: '0.6cqw',
                padding: '0.1cqw 0.45cqw',
                borderRadius: RADIUS.sm,
                background: c.liked ? 'rgba(232, 80, 80, 0.12)' : `${GOLD} 0.06)`,
                border: `1px solid ${c.liked ? 'rgba(232, 80, 80, 0.45)' : `${GOLD} 0.25)`}`,
                color: c.liked ? '#e85050' : TEXT_MUTED,
                opacity: likeBusy ? 0.6 : 1,
                cursor: 'pointer',
              }}
              title={c.liked ? t('Unlike') : t('Like')}
              aria-label={c.likes === 1 ? t('1 like') : t('{n} likes', { n: c.likes })}
            >
              <HeartIcon filled={c.liked} size="0.75cqw" />
              <span style={{ fontWeight: c.liked ? 600 : 500 }}>{c.likes}</span>
            </button>
          ) : null}
        </div>
      </div>

    </div>
  );
}

function HeartIcon({ filled, size }) {
  return (
    <svg viewBox="0 0 24 24" style={{ width: size, height: size }} fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

// Nearest ancestor that scrolls vertically, or null.
function scrollParent(el) {
  for (let p = el?.parentElement; p; p = p.parentElement) {
    const o = getComputedStyle(p).overflowY;
    if (o === 'auto' || o === 'scroll') return p;
  }
  return null;
}

// Where the pill's anchor stops while its row scrolls out: half the pill
// plus a hair, so the pill sits just inside the list's top edge.
const TOOLBAR_STICK = '0.95cqw';

// The pill's inset on every side, and its corner. A control's highlight is
// concentric with the pill: its radius is the pill's minus the inset and
// the 1px border, so the two curves share a centre.
const TOOLBAR_PAD = '0.2cqw';
const TOOLBAR_RADIUS = RADIUS.xl;
const TOOL_RADIUS = innerRadius(TOOLBAR_RADIUS, `calc(${TOOLBAR_PAD} + 1px)`);

// One flat control inside the hover pill. The pill carries the border and
// background; hover, press and open states live in app.css (.chat-tool).
function ToolbarButton({ className = '', style, children, ...rest }) {
  return (
    <button
      type="button"
      {...rest}
      className={`chat-tool flex items-center justify-center cursor-pointer ${className}`}
      style={{
        width: '1.3cqw',
        height: '1.3cqw',
        borderRadius: TOOL_RADIUS,
        border: 'none',
        color: TEXT_MUTED,
        padding: 0,
        ...style,
      }}
    >
      {children}
    </button>
  );
}

function ReactionToolButton({ emoji, mine, onClick, tipOn, ...rest }) {
  const name = t(REACTION_NAMES[emoji] || emoji);
  return (
    <ToolbarButton
      {...rest}
      {...tipOn?.(mine ? t('Remove {name}', { name: name.toLowerCase() }) : name)}
      onClick={onClick}
      className={`chat-tool-emoji${mine ? ' is-mine' : ''}`}
      aria-label={mine ? t('Remove {emoji}', { emoji }) : t('React {emoji}', { emoji })}
      aria-pressed={mine}
    >
      <EmojiGlyph emoji={emoji} size="0.85cqw" />
    </ToolbarButton>
  );
}

// Modern chat-app composer: a single rounded container holds the editor on
// top and the send button anchored to its bottom-right corner. No separate
// pill + circle pair; the send button is part of the input frame the way it
// is in Claude / ChatGPT / iMessage. The composer's own border already
// terminates the comment list cleanly, so no horizontal rule above it.
export function CommentComposer({
  value,
  onChange,
  onSubmit,
  posting = false,
  placeholder = t('Add a comment...'),
  // Editor grows with the draft up to this, then scrolls internally.
  maxHeight = '10cqw',
  autoFocus = false,
  // Changes to this focus the editor after mount (see MarkdownEditor).
  focusKey = 0,
  sorceryCards,
  onOpenCard,
  onCardLinkHover,
  onCardLinkHoverEnd,
  mentionPlayers,
  radius = 'var(--radius-xl)',
  children = null,
  // A control anchored bottom-left inside the frame, mirroring the send
  // button (an attach button), and whether an empty body may still be
  // sent (it carries an attachment).
  leading = null,
  canSendEmpty = false,
  // Discord-style context strip ("Replying to X") docked onto the top of
  // the frame: the strip takes the top corners and the frame squares its
  // own so the two read as one box. Rows inside are `ComposerBar`s.
  attached = null,
}) {
  const canSend = !posting && (canSendEmpty || !!value.trim());
  return (
    <div className="shrink-0">
      {children}
      {attached ? (
        <div
          style={{
            background: 'rgba(0,0,0,0.22)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderBottom: 'none',
            borderRadius: `${radius} ${radius} 0 0`,
            padding: '0.15cqw 0.55cqw',
          }}
        >
          {attached}
        </div>
      ) : null}
      <div
        className="flex flex-col relative"
        style={{
          background: 'rgba(255,255,255,0.035)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: attached ? `0 0 ${radius} ${radius}` : radius,
          // Uniform 0.55cqw padding on every side so the send button
          // (overlay'd at bottom-right) sits at exactly the same distance
          // from both the bottom and right card edges.
          padding: '0.55cqw',
          transition: 'border-color 160ms ease',
        }}
        onFocusCapture={(e) => { e.currentTarget.style.borderColor = `${GOLD} 0.35)`; }}
        onBlurCapture={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
      >
        <MarkdownEditor
          value={value}
          onChange={onChange}
          onSubmit={() => { if (canSend) onSubmit(); }}
          placeholder={placeholder}
          autoFocus={autoFocus}
          focusKey={focusKey}
          sorceryCards={sorceryCards}
          onOpenCard={onOpenCard}
          onCardLinkHover={onCardLinkHover}
          onCardLinkHoverEnd={onCardLinkHoverEnd}
          mentionPlayers={mentionPlayers}
          style={{ color: TEXT_BODY, lineHeight: 1.4, maxHeight }}
        />
        {/* Toolbar in flow under the editor (ChatGPT / Claude composer):
            the editor grows line by line until maxHeight, then scrolls, and
            the buttons never sit over the text. */}
        <div className="flex items-center justify-between shrink-0" style={{ marginTop: '0.4cqw' }}>
        {leading || <span />}
        <button
          type="button"
          className="flex items-center justify-center cursor-pointer transition-all"
          style={{
            width: '1.85cqw',
            height: '1.85cqw',
            // Visually-matched curvature against the composer container:
            // 0.8cqw on the frame, ~0.45cqw on the 1.85cqw button. A strict
            // proportional scale (~0.25cqw) read too sharp next to the
            // container's softer corners; this lands between sharp and
            // circular and looks like the same corner family.
            borderRadius: 'var(--radius-md)',
            background: canSend
              ? 'linear-gradient(180deg, rgba(212,168,67,0.95) 0%, rgba(160,120,40,0.95) 100%)'
              : 'rgba(255,255,255,0.05)',
            border: `1px solid ${canSend ? 'rgba(212,168,67,0.6)' : 'rgba(255,255,255,0.1)'}`,
            color: canSend ? '#0e0a06' : TEXT_MUTED,
            opacity: posting ? 0.5 : 1,
            boxShadow: canSend
              ? '0 2px 6px rgba(212, 168, 67, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
              : 'none',
          }}
          disabled={!canSend}
          onClick={onSubmit}
          title={t('Post comment (Enter)')}
          aria-label={t('Post comment')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" style={{ width: '0.95cqw', height: '0.95cqw' }}>
            <line x1="12" y1="19" x2="12" y2="5" />
            <polyline points="5 12 12 5 19 12" />
          </svg>
        </button>
        </div>
      </div>
    </div>
  );
}

// One row of the composer's docked strip: a truncating label and the round
// dismiss on the right, the way Discord's reply bar clears.
export function ComposerBar({ children, onClear, clearLabel = t('Clear') }) {
  return (
    <div className="flex items-center gap-2 text-[0.58cqw]" style={{ color: TEXT_MUTED, padding: '0.25cqw 0' }}>
      <span className="flex-1 min-w-0 truncate">{children}</span>
      <button
        type="button"
        className="shrink-0 flex items-center justify-center cursor-pointer transition-colors"
        style={{
          width: '0.95cqw',
          height: '0.95cqw',
          borderRadius: RADIUS.full,
          background: 'rgba(255,255,255,0.14)',
          border: 'none',
          color: TEXT_BODY,
          padding: 0,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.28)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.14)'; }}
        onClick={onClear}
        title={clearLabel}
        aria-label={clearLabel}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" style={{ width: '0.5cqw', height: '0.5cqw' }}>
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>
    </div>
  );
}
